import { useEffect, useState } from "react"
import { ArrowLeftRight, X } from "lucide-react"
import { StatusPill, formatDateTime } from "procurement/design"
import {
  useGetBookInventoryTransferSourcesQuery,
  useCreateBookInventoryTransferMutation,
} from "shared/redux/endpoints/procurementItems"
import { showToastSuccess, showToastError } from "utilities/toastUtil"
import { getApiErrorMessage } from "../../orderSubmitFeedback"
import { Button, NumberInput, Textarea } from "../../ui"
import { formatQty, shortageQuantity } from "../../bookInventory/model"

const TRANSFER_STATUS_PILL = {
  READY_FOR_TRANSFER_CHECK: { tone: "success", label: "Có thể chuyển" },
  RESERVATION_INCOMPLETE: { tone: "warning", label: "Còn lớp chưa giữ đủ sách" },
  RESERVED_EXCEEDS_STOCK: { tone: "danger", label: "Đã giữ vượt tồn kho" },
}

const num = (value) => Number(value) || 0

// crypto.randomUUID only exists on secure origins (https / localhost).
const newRequestKey = () => {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID()
  const b = crypto.getRandomValues(new Uint8Array(16))
  b[6] = (b[6] & 0x0f) | 0x40
  b[8] = (b[8] & 0x3f) | 0x80
  const hex = [...b].map((x) => x.toString(16).padStart(2, "0")).join("")
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

// Transfer one book into the row's center (the destination) from another
// center that has transferable stock.
const TransferStockModal = ({ open, row, daysWindow, onClose }) => {
  const canLoad = open && row?.centerId != null && row?.itemId != null
  const { data: sources, isFetching, isError, refetch } = useGetBookInventoryTransferSourcesQuery(
    { itemId: row?.itemId, currentCenterId: row?.centerId },
    { skip: !canLoad }
  )
  const [createTransfer, { isLoading: saving }] = useCreateBookInventoryTransferMutation()
  const [sourceId, setSourceId] = useState(null)
  const [quantity, setQuantity] = useState("")
  const [note, setNote] = useState("")

  useEffect(() => {
    setSourceId(null)
    setQuantity("")
    setNote("")
  }, [open, row?.centerId, row?.itemId])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (event) => {
      if (event.key === "Escape") onClose()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [open, onClose])

  if (!open || !row) return null

  const rows = sources || []
  const shortage = shortageQuantity(row, daysWindow)
  const source = rows.find((s) => s.sourceCenterId === sourceId) || null
  const maxQty = num(source?.transferableQty)
  const qty = Number(quantity)
  const valid = !!source && Number.isInteger(qty) && qty > 0 && qty <= maxQty

  const pick = (s) => {
    setSourceId(s.sourceCenterId)
    const available = num(s.transferableQty)
    setQuantity(String(shortage > 0 ? Math.min(shortage, available) : available))
  }

  const confirm = async () => {
    if (!valid || saving) return
    try {
      const result = await createTransfer({
        requestKey: newRequestKey(),
        itemId: row.itemId,
        sourceCenterId: source.sourceCenterId,
        destinationCenterId: row.centerId,
        quantity: qty,
        note: note.trim() || undefined,
      }).unwrap()
      showToastSuccess(
        `Đã chuyển ${formatQty(qty)} cuốn từ ${source.sourceCenterName}. Tồn kho mới: ${formatQty(
          result?.destinationStockAfter
        )} cuốn.`
      )
      onClose()
    } catch (e) {
      showToastError(getApiErrorMessage(e, "Không chuyển được sách. Vui lòng thử lại."))
      if (e?.status === 409) refetch()
    }
  }

  return (
    <div className="ds-drawer-root">
      <div className="ds-drawer-backdrop bi-modal-backdrop" onClick={onClose} aria-hidden />
      <div className="bi-modal bi-modal--stock" role="dialog" aria-modal="true" aria-label="Transfer stock">
        <div className="bi-modal-head">
          <span className="bi-modal-icon" aria-hidden>
            <ArrowLeftRight />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 className="pc-h1" style={{ fontSize: 18 }}>Transfer stock</h2>
            <div className="bi-muted" style={{ margin: 0 }}>
              Chuyển {row.itemName || "sách"} về {row.centerName || `Center ${row.centerId}`}
              {shortage > 0 ? ` · đang thiếu ${formatQty(shortage)} cuốn` : ""}
            </div>
          </div>
          <button type="button" className="pc-btn pc-btn--ghost pc-btn--sm" onClick={onClose} aria-label="Close">
            <X size={16} aria-hidden />
          </button>
        </div>

        <div className="bi-modal-body">
          <div className="bi-section-title">Center nguồn</div>
          {isError ? (
            <p className="bi-note">
              Không tải được danh sách center.{" "}
              <button type="button" className="pc-btn pc-btn--ghost pc-btn--sm" onClick={refetch}>
                Thử lại
              </button>
            </p>
          ) : !sources ? (
            <p className="bi-muted">Đang tải…</p>
          ) : rows.length === 0 ? (
            <p className="bi-muted">Không có center nào khác đang có sách này.</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="bi-class-table">
                <thead>
                  <tr>
                    <th aria-label="Chọn" />
                    <th>Center</th>
                    <th className="is-num">Stock</th>
                    <th className="is-num">Reserved chưa phát</th>
                    <th className="is-num">Có thể chuyển</th>
                    <th>Trạng thái</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((s) => {
                    const disabled = num(s.transferableQty) <= 0
                    const pill = TRANSFER_STATUS_PILL[s.transferStatus] || { tone: "neutral", label: s.transferStatus || "—" }
                    return (
                      <tr
                        key={s.sourceCenterId}
                        onClick={() => !disabled && pick(s)}
                        style={{ cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1 }}
                      >
                        <td>
                          <input
                            type="radio"
                            name="transfer-source"
                            aria-label={s.sourceCenterName}
                            checked={sourceId === s.sourceCenterId}
                            disabled={disabled}
                            onChange={() => pick(s)}
                          />
                        </td>
                        <td>
                          <div style={{ fontWeight: 600, color: "var(--text-primary)" }}>{s.sourceCenterName}</div>
                          {s.stockUpdatedAt && (
                            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                              Cập nhật {formatDateTime(s.stockUpdatedAt)}
                            </div>
                          )}
                        </td>
                        <td className="is-num">{formatQty(s.currentStock)}</td>
                        <td className="is-num">{formatQty(s.outstandingReservedQty)}</td>
                        <td className="is-num">
                          <strong>{formatQty(s.transferableQty)}</strong>
                        </td>
                        <td>
                          <StatusPill tone={pill.tone} label={pill.label} />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          {source && (
            <div className="bi-modal-grid" style={{ marginTop: 14 }}>
              <NumberInput
                label="Số lượng chuyển"
                required
                value={quantity}
                onChange={setQuantity}
                hint={`Tối đa ${formatQty(maxQty)} cuốn từ ${source.sourceCenterName}.`}
              />
              <Textarea label="Ghi chú" rows={2} value={note} onChange={(event) => setNote(event.target.value)} />
            </div>
          )}
        </div>

        <div className="bi-modal-foot">
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            leftIcon={<ArrowLeftRight size={15} aria-hidden />}
            disabled={!valid || saving || isFetching}
            onClick={confirm}
          >
            {saving ? "Đang chuyển…" : "Confirm Transfer"}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default TransferStockModal
